export interface UserCapabilities {
  readonly canViewVms: boolean
  readonly canViewUsers: boolean
  readonly canViewStorages: boolean
  readonly canViewPublicIps: boolean
  readonly canViewInvitations: boolean
  readonly canManageSettings: boolean
  readonly canManageUsers: boolean
  readonly canViewRoles: boolean
  readonly canManageRoles: boolean
  readonly canManageVms: boolean
  readonly canManageStorages: boolean
  readonly canManagePublicIps: boolean
  readonly canWhitelistUsers: boolean
  readonly canFreezeUserBalance: boolean
  readonly canIncreaseUserBalance: boolean
  readonly canReturnToBalance: boolean
  readonly canRevokeFreeCredits: boolean
  readonly canMoveToLimbo: boolean
  readonly canTerminateVms: boolean
}

// 'admin' (coarse) or any 'admin:*' scope counts as admin access.
const ADMIN_SCOPE = /^admin(?::|$)/

export function hasAdminAccess(permissions: readonly string[]): boolean {
  return permissions.some((p) => ADMIN_SCOPE.test(p))
}

export function hasPermission(
  permissions: readonly string[],
  required: string,
): boolean {
  return permissions.includes('admin') || permissions.includes(required)
}

export function createUserCapabilities(
  permissions: string[],
): UserCapabilities {
  const has = (p: string) => hasPermission(permissions, p)
  return {
    canViewVms: has('admin:vms:read'),
    canViewUsers: has('admin:users:read'),
    canViewStorages: has('admin:storages:read'),
    canViewPublicIps: has('admin:public_ips:read'),
    canViewInvitations: has('admin:invites:read'),
    canManageSettings: has('admin:settings:write'),
    canManageUsers: has('admin:users:write'),
    canViewRoles: has('admin:roles:read'),
    canManageRoles: has('admin:roles:write'),
    canManageVms: has('admin:vms:write'),
    canManageStorages: has('admin:storages:write'),
    canManagePublicIps: has('admin:public_ips:write'),
    canWhitelistUsers: has('admin:invites:write'),
    canFreezeUserBalance: has('admin:users:balance:freeze'),
    canIncreaseUserBalance: has('admin:users:balance:increase'),
    canMoveToLimbo: has('admin:users:balance:limbo'),
    canRevokeFreeCredits: has('admin:users:balance:limbo'),
    canReturnToBalance: has('admin:users:balance:limbo'),
    canTerminateVms: has('admin:vms:write'),
  }
}
